/**
 * NotesPhotoStrip — Phase 13 Ask 3 (2026-07-30)
 *
 * Reusable image-strip for text-note surfaces that don't have their
 * own photo field. Reuses the `mood_board` collection via `scope` +
 * `scope_id` — no schema change.
 *
 * Scopes used:
 *   `scene_art_notes`        (scope_id = scene.id)
 *   `scene_additional_notes` (scope_id = scene.id)
 *   `item_notes`             (scope_id = breakdown_item.id)
 *
 * Constraints honoured:
 *   • Upload via existing `POST /mood-board/upload` — that endpoint
 *     already calls `register_upload_file()` internally, no duplicate
 *     upload paths.
 *   • Render via `<AuthImage>` + dtoken (implicit — AuthImage handles
 *     dtoken minting on its own).
 *   • Delete via existing `DELETE /mood-board/{id}`.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AuthImage } from '../../components/AuthImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotesPhotoStrip({
  projectId, token, scope, scopeId, canWrite = true, testKey = 'notes-photo-strip',
}) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!projectId || !token) return;
    try {
      const r = await axios.get(`${API}/projects/${projectId}/mood-board`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const all = Array.isArray(r.data) ? r.data : (r.data?.images || []);
      setPhotos(all.filter(p => p.scope === scope && p.scope_id === scopeId));
    } catch (e) {
      // silent — an empty strip is fine while list endpoint is transient
    }
  }, [projectId, token, scope, scopeId]);

  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !scopeId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const url = `${API}/projects/${projectId}/mood-board/upload?department=ART&scope=${encodeURIComponent(scope)}&scope_id=${encodeURIComponent(scopeId)}`;
      await axios.post(url, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      await load();
      toast.success('Photo attached');
    } catch (err) {
      console.error(err);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/mood-board/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPhotos(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error(err);
      toast.error('Delete failed');
    }
  };

  return (
    <div className="mt-2" data-testid={testKey}>
      <div className="flex gap-1 flex-wrap items-center">
        {photos.map(p => (
          <div key={p.id} className="relative group w-[80px] h-[80px] rounded overflow-hidden border border-[var(--color-border)]">
            <AuthImage path={p.storage_path} className="w-full h-full object-cover" alt="" />
            {canWrite && (
              <button type="button"
                onClick={() => deletePhoto(p.id)}
                className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 bg-black/70 text-[var(--color-priority-critical)] rounded p-0.5"
                title="Delete"
                data-testid={`${testKey}-delete-${p.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
        {canWrite && (
          <button type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading || !scopeId}
            className="w-[80px] h-[80px] rounded border border-dashed border-[var(--color-border-strong)] hover:border-[var(--color-accent)] text-[var(--color-text-dim)] hover:text-[var(--color-accent)] flex flex-col items-center justify-center gap-1 text-[10px] disabled:opacity-40"
            title="Attach photo"
            data-testid={`${testKey}-upload`}>
            <Upload className="w-3 h-3" />
            {uploading ? '…' : 'Attach'}
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
    </div>
  );
}
